import { useEffect, useMemo, useState } from "react";
import {
  C,
  CLIENT_CUST_PRIO,
  DEPARTMENTS,
  FINANCIAL_MAP,
  GROWTH_MAP,
  PHASES,
  TODAY,
} from "../../../../constants";
import {
  daysAgo,
  daysUntil,
  fmtMoney,
  getPhaseProgress,
  getDeptSummary,
  getInit,
  priorityLabel,
  projectPriority,
  withUrgency,
} from "../../../../utils";
import apiClient from "../../../../services/api";
import { useSystemSettings } from "../../../../app/providers/SystemSettingsProvider";
import { mapProject, mapTask, taskStatusToEnum } from "../../../../mappers";
import RichCommentInput from "../../../../shared/comments/RichCommentInput";
import { CommentContent } from "../../../../shared/comments/commentContent";

const inpStyle = { width: "100%", padding: "4px 8px", background: "#070e20", border: "1px solid #1c2e52", borderRadius: 6, color: "#dce8f8", fontSize: 12, fontFamily: "inherit", outline: "none", colorScheme: "dark" };
const selStyle = { ...inpStyle, fontWeight: 600 };

function EditRow({ label, children }) {
  return (
    <div className="det-row">
      <span className="det-key">{label}</span>
      <span className="det-val" style={{ minWidth: 0, flex: 1 }}>{children}</span>
    </div>
  );
}

export function ProjectsPage({
  projects,
  setProjects,
  consultants,
  tasks,
  setTasks,
  showToast,
  nav,
  KpiCardComponent,
  WorkflowDrawerComponent,
  StatusChipComponent,
  DepartmentBadgeComponent,
  PriorityBadgeComponent,
  CustomerPriorityTagComponent,
  ChipComponent,
  AvatarComponent,
}) {
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("All");
  const [reportF, setReportF] = useState("All");
  const [deptF, setDeptF] = useState("All");
  const [prioF, setPrioF] = useState("All");
  const [growthF, setGrowthF] = useState("All");
  const [finF, setFinF] = useState("All");
  const [serviceF, setServiceF] = useState("All");
  const [sortCol, setSortCol] = useState("_priority");
  const [sortDir, setSortDir] = useState(-1);
  const [exp, setExp] = useState(new Set());

  const tasksU = useMemo(() => withUrgency(tasks, projects), [tasks, projects]);
  const openT = tasksU.filter((task) => task.status !== "Done" && task.status !== "Cancelled");
  const odT = openT.filter((task) => daysUntil(task.dueDate) < 0);
  const blT = openT.filter((task) => task.status === "Blocked");
  const wkT = openT.filter((task) => {
    const days = daysUntil(task.dueDate);
    return days >= 0 && days <= 7;
  });
  const ccT = openT.filter((task) => task.customerPriority === "Escalated" || task.customerPriority === "VIP");
  const deptSum = getDeptSummary(openT);
  const bottleneck = Object.entries(deptSum).sort((left, right) => (right[1].overdue + right[1].blocked) - (left[1].overdue + left[1].blocked))[0];
  const total = tasksU.filter((task) => task.status !== "Cancelled").length;
  const done = tasksU.filter((task) => task.status === "Done").length;
  const wfPct = total > 0 ? Math.round((done / total) * 100) : 0;

  const totalB = projects.reduce((sum, project) => sum + (project.billables || 0), 0);
  // Effective NPS = per-project override, falling back to Client.nps.
  // 0 is treated as "not set" (pre-Phase B default + not a valid WBR
  // rating), so we fall through to clientNps in that case too.
  const effNps = (p) => {
    if (typeof p.NPS === "number" && p.NPS > 0) return p.NPS;
    if (typeof p.clientNps === "number" && p.clientNps > 0) return p.clientNps;
    return null;
  };
  const withUtil = projects.filter(p => (p.utilizationPct || 0) > 0);
  const withNPS = projects.map(effNps).filter((v) => typeof v === "number" && v > 0);
  const withP = projects.filter(p => (p.profitPct || 0) > 0);
  const avgUtil = withUtil.length > 0 ? Math.round(withUtil.reduce((sum, p) => sum + p.utilizationPct, 0) / withUtil.length) : 0;
  const avgNPS = withNPS.length > 0 ? (withNPS.reduce((sum, v) => sum + v, 0) / withNPS.length).toFixed(1) : "—";
  const avgP = withP.length > 0 ? Math.round(withP.reduce((sum, p) => sum + p.profitPct, 0) / withP.length) : 0;

  function toggleSort(col) {
    if (sortCol === col) setSortDir((value) => -value);
    else {
      setSortCol(col);
      setSortDir(col === "projectName" ? 1 : -1);
    }
  }

  function toggleExp(id) {
    setExp((state) => {
      const next = new Set(state);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const rows = [...projects]
    .map((project) => ({ ...project, _priority: projectPriority(project) }))
    .filter((project) => {
      if (statusF !== "All" && project.projectStatus !== statusF) return false;
      if (reportF !== "All" && project.weeklyReportStatus !== reportF) return false;
      if (prioF !== "All" && priorityLabel(project._priority) !== prioF) return false;
      if (deptF !== "All" && !tasks.some((task) => task.projectId === project.projectId && task.department === deptF)) return false;
      if (growthF !== "All" && project.growthStatus !== growthF) return false;
      if (finF !== "All" && project.financialStatus !== finF) return false;
      if (serviceF !== "All" && project.serviceType !== serviceF) return false;
      if (search && !project.projectName.toLowerCase().includes(search.toLowerCase()) && !project.clientName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((left, right) => {
      const leftValue = left[sortCol];
      const rightValue = right[sortCol];
      return (typeof leftValue === "number" ? leftValue - rightValue : String(leftValue).localeCompare(String(rightValue))) * sortDir;
    });

  async function doneTask(taskId) {
    const today = new Date().toISOString().slice(0, 10);
    // Optimistic update so the checkbox reflects immediately.
    setTasks((state) => state.map((task) => (task.taskId === taskId ? { ...task, status: "Done", completedDate: today } : task)));
    try {
      await apiClient.updateTask(taskId, { status: taskStatusToEnum("Done"), completedDate: today });
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Could not mark task done", "error");
      // Roll back on failure.
      setTasks((state) => state.map((task) => (task.taskId === taskId ? { ...task, status: "To Do", completedDate: null } : task)));
    }
  }

  function SortHeader({ col, children }) {
    const active = sortCol === col;
    return <th className={active ? "sorted" : ""} onClick={() => toggleSort(col)}>{children}{active ? (sortDir === 1 ? " ↑" : " ↓") : ""}</th>;
  }

  return (
    <div className="fade">
      <div className="pg-top">
        <div className="pg-title">Delivery</div>
        <div className="pg-sub">Project health, client satisfaction, workflow execution, and commercial performance</div>
      </div>
      <div className="pg-body">
        <div className="krow" style={{ marginBottom: 10 }}>
          <KpiCardComponent label="Total Billables" value={fmtMoney(totalB)} trend="+8%" up />
          <KpiCardComponent label="Avg Utilisation" value={`${avgUtil}%`} trend="−3pt" up={false} />
          <KpiCardComponent label="Avg NPS" value={avgNPS} trend="+0.3" up />
          <KpiCardComponent label="Avg Profitability" value={`${avgP}%`} trend="+2pt" up />
        </div>
        <div className="krow7" style={{ marginBottom: 22 }}>
          <KpiCardComponent label="Open Tasks" value={openT.length} accent={C.blue} />
          <KpiCardComponent label="Overdue" value={odT.length} accent={odT.length > 0 ? C.red : C.green} onClick={() => { setPrioF("All"); setStatusF("All"); }} />
          <KpiCardComponent label="Blocked" value={blT.length} accent={blT.length > 0 ? C.yellow : C.green} />
          <KpiCardComponent label="Due This Week" value={wkT.length} accent={C.yellow} />
          <KpiCardComponent label="Critical Cust." value={ccT.length} accent={ccT.length > 0 ? C.red : C.green} />
          <KpiCardComponent label="Workflow %" value={`${wfPct}%`} accent={wfPct > 70 ? C.green : wfPct > 40 ? C.yellow : C.red} />
          <KpiCardComponent label="Bottleneck" value={bottleneck ? bottleneck[0].split(" ")[0] : "None"} accent={bottleneck && bottleneck[1].overdue > 0 ? C.red : C.yellow} sub={bottleneck ? `${bottleneck[1].overdue} overdue` : ""} />
        </div>
        <div className="fbar">
          <input className="finput" placeholder="Search projects…" value={search} onChange={(event) => setSearch(event.target.value)} style={{ width: 220 }} />
          <select className="fsel" value={statusF} onChange={(event) => setStatusF(event.target.value)}>
            {["All", "On Track", "At Risk", "Critical"].map((status) => <option key={status}>{status}</option>)}
          </select>
          <select className="fsel" value={reportF} onChange={(event) => setReportF(event.target.value)}>
            {["All", "Submitted", "Pending", "Overdue"].map((status) => <option key={status}>{status}</option>)}
          </select>
          <select className="fsel" value={deptF} onChange={(event) => setDeptF(event.target.value)}>
            <option value="All">All Departments</option>
            {DEPARTMENTS.map((dept) => <option key={dept}>{dept}</option>)}
          </select>
          <select className="fsel" value={prioF} onChange={(event) => setPrioF(event.target.value)}>
            {["All", "P1", "P2", "P3"].map((status) => <option key={status}>{status}</option>)}
          </select>
          <select className="fsel" value={serviceF} onChange={(event) => setServiceF(event.target.value)}>
            <option value="All">All Services</option>
            {["CONSULTING", "VALUE SCAN", "TRANSFORMATION SCAN", "AI PROJECT"].map((status) => <option key={status}>{status}</option>)}
          </select>
          <select className="fsel" value={growthF} onChange={(event) => setGrowthF(event.target.value)}>
            <option value="All">All Growth</option>
            {["STABLE", "NEEDS ATTENTION", "RENEGOTIATION", "INACTIVE"].map((status) => <option key={status}>{status}</option>)}
          </select>
          <select className="fsel" value={finF} onChange={(event) => setFinF(event.target.value)}>
            <option value="All">All Financial</option>
            {["Stable", "Needs Attention"].map((status) => <option key={status}>{status}</option>)}
          </select>
          <div className="finfo"><strong>{rows.length}</strong> project{rows.length !== 1 ? "s" : ""}</div>
          <button className="btn-add" style={{ marginLeft: "auto" }} onClick={() => nav("new-project")}>+ New Project</button>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <SortHeader col="projectName">Project</SortHeader>
                <SortHeader col="_priority">Priority</SortHeader>
                <th>Status</th>
                <th>Workflow</th>
                <th>Hours</th>
                <SortHeader col="billables">Billables</SortHeader>
                <SortHeader col="NPS">NPS</SortHeader>
                <th>Report</th>
                <th>Check-in</th>
                <SortHeader col="utilizationPct">Util.</SortHeader>
                <SortHeader col="contractEndDate">Contract End</SortHeader>
                <SortHeader col="profitPct">Profit</SortHeader>
                <th>Service</th>
                <th>Growth</th>
                <th>Financial</th>
                <th>Payment</th>
                <th>Raincoach</th>
                <th>Stakeholder</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((project) => {
                const pl = priorityLabel(project._priority);
                const dEnd = daysUntil(project.contractEndDate);
                const pt = tasksU.filter((task) => task.projectId === project.projectId && task.status !== "Cancelled");
                const doneP = pt.filter((task) => task.status === "Done").length;
                const wfP = pt.length > 0 ? Math.round((doneP / pt.length) * 100) : 0;
                const odP = pt.filter((task) => daysUntil(task.dueDate) < 0 && task.status !== "Done").length;
                const blP = pt.filter((task) => task.status === "Blocked").length;
                const isExp = exp.has(project.projectId);
                const cp = CLIENT_CUST_PRIO[project.clientName] || "Standard";
                const est = project.estHours || 0;
                const logged = project.loggedHours || 0;
                const hpw = project.hoursPerWeek || 1;
                const hPct = est > 0 ? Math.min(100, Math.round((logged / est) * 100)) : 0;
                const remaining = Math.max(0, est - logged);
                const weeksLeft = hpw > 0 ? Math.ceil(remaining / hpw) : null;
                const projComplete = weeksLeft != null ? new Date(TODAY.getTime() + weeksLeft * 7 * 86400000).toISOString().slice(0, 10) : null;
                const burnColor = hPct >= 90 ? C.red : hPct >= 70 ? C.yellow : C.green;

                return (
                  <FragmentRow
                    key={project.projectId}
                    expanded={isExp}
                    row={(
                      <tr className={`trow${pl === "P1" ? " p1r" : pl === "P2" ? " p2r" : ""}`} onClick={(event) => { if (!event.target.closest(".expbtn")) nav("project", project.projectId); }}>
                        <td>
                          <button className="expbtn" style={{ background: "none", border: "none", cursor: "pointer", color: C.dim, fontSize: 12, padding: "2px 4px", borderRadius: 4 }} onClick={(event) => { event.stopPropagation(); toggleExp(project.projectId); }}>
                            {isExp ? "▼" : "▶"}
                          </button>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: C.text }}>{project.projectName}</div>
                          <div style={{ fontSize: 11, color: C.sub, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                            {project.clientName}
                            <CustomerPriorityTagComponent cp={cp} />
                          </div>
                        </td>
                        <td><PriorityBadgeComponent score={project._priority} /></td>
                        <td><ChipComponent status={project.projectStatus} /></td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ width: 52, height: 4, background: "#14213d", borderRadius: 2, overflow: "hidden" }}>
                              <div style={{ width: `${wfP}%`, height: "100%", borderRadius: 2, background: wfP > 70 ? C.green : wfP > 40 ? C.blue : C.dim }} />
                            </div>
                            <span className="mono" style={{ fontSize: 10, color: C.dim }}>{wfP}%</span>
                            {odP > 0 && <span className="bcrit">{odP}od</span>}
                            {blP > 0 && <span className="bwarn">{blP}bl</span>}
                          </div>
                        </td>
                        <td className="hrs-cell">
                          {est > 0 ? (
                            <>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                <span className="mono" style={{ fontSize: 11, color: burnColor }}>{logged.toLocaleString()}</span>
                                <span className="mono" style={{ fontSize: 10, color: C.dim }}>/{est.toLocaleString()}h</span>
                              </div>
                              <div className="hrs-bar"><div className="hrs-fill" style={{ width: `${hPct}%`, background: burnColor }} /></div>
                              <div className="hrs-stat">{hpw}h/wk{projComplete ? ` · ~${projComplete}` : ""}</div>
                            </>
                          ) : logged > 0 ? (
                            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                              <span className="mono" style={{ fontSize: 11, color: C.blue }}>{logged.toLocaleString()}</span>
                              <span className="mono" style={{ fontSize: 10, color: C.dim }}>h · no est</span>
                            </div>
                          ) : <span style={{ fontSize: 11, color: C.dim }}>—</span>}
                        </td>
                        <td>{project.billables > 0 ? <span className="mono" style={{ color: C.blue }}>{fmtMoney(project.billables)}</span> : <span style={{ color: C.dim }}>—</span>}</td>
                        <td>{(() => {
                          const n = effNps(project);
                          return typeof n === "number" && n > 0
                            ? <span className="mono" style={{ fontWeight: 700, color: n >= 4 ? C.green : n >= 3 ? C.yellow : C.red }}>{n}</span>
                            : <span style={{ color: C.dim }}>—</span>;
                        })()}</td>
                        <td><ChipComponent status={project.weeklyReportStatus} /></td>
                        <td>{project.lastClientCheckin ? <span className="mono" style={{ fontSize: 12, color: daysAgo(project.lastClientCheckin) > 7 ? C.red : C.sub }}>{project.lastClientCheckin}</span> : <span style={{ color: C.dim }}>—</span>}</td>
                        <td>
                          {project.utilizationPct > 0 ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <span className="mono" style={{ fontSize: 12, minWidth: 30 }}>{project.utilizationPct}%</span>
                              <div className="minibar"><div className="minibar-fill" style={{ width: `${project.utilizationPct}%`, background: project.utilizationPct >= 80 ? C.green : project.utilizationPct >= 60 ? C.yellow : C.red }} /></div>
                            </div>
                          ) : <span style={{ color: C.dim }}>—</span>}
                        </td>
                        <td>
                          {project.contractEndDate ? (
                            <>
                              <div className="mono" style={{ fontSize: 12, color: dEnd < 45 ? C.yellow : C.text }}>{project.contractEndDate}</div>
                              {dEnd < 45 && <span className="bwarn" style={{ marginTop: 3, display: "inline-flex" }}>{dEnd}d</span>}
                            </>
                          ) : <span style={{ color: C.dim }}>—</span>}
                        </td>
                        <td>{project.profitPct !== 0 ? <span className="mono" style={{ fontWeight: 700, color: project.profitPct >= 30 ? C.green : project.profitPct >= 15 ? C.yellow : C.red }}>{project.profitPct}%</span> : <span style={{ color: C.dim }}>—</span>}</td>
                        <td><ChipComponent status={project.serviceType} /></td>
                        <td>{project.growthStatus ? (() => { const s = GROWTH_MAP[project.growthStatus] || GROWTH_MAP[(project.growthStatus || "").replace(/_/g, " ")] || { fg: "#c4d4ee", bg: "rgba(143,164,200,.14)" }; return <div style={{ display: "inline-flex", alignItems: "center", gap: 2, padding: "3px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, color: s.fg, background: s.bg, border: `1px solid ${s.fg}22` }}>{project.growthStatus.replace(/_/g, " ")}</div>; })() : <span style={{ color: C.dim }}>—</span>}</td>
                        <td>{project.financialStatus ? (() => { const s = FINANCIAL_MAP[project.financialStatus] || FINANCIAL_MAP[(project.financialStatus || "").replace(/_/g, " ")] || { fg: "#c4d4ee", bg: "rgba(143,164,200,.14)" }; return <div style={{ display: "inline-flex", alignItems: "center", gap: 2, padding: "3px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, color: s.fg, background: s.bg, border: `1px solid ${s.fg}22` }}>{project.financialStatus.replace(/_/g, " ")}</div>; })() : <span style={{ color: C.dim }}>—</span>}</td>
                        <td><ChipComponent status={project.paymentSolution} /></td>
                        <td>{project.raincoach ? <span style={{ fontSize: 11, color: "#c4d4ee" }}>{project.raincoach}</span> : <span style={{ color: C.dim }}>—</span>}</td>
                        <td>{project.stakeholder ? <span style={{ fontSize: 11, color: "#c4d4ee" }}>{project.stakeholder}</span> : <span style={{ color: C.dim }}>—</span>}</td>
                      </tr>
                    )}
                    drawer={isExp ? <WorkflowDrawerComponent projectId={project.projectId} tasks={tasksU} onDone={doneTask} colSpan={19} StatusChip={StatusChipComponent} DepartmentBadge={DepartmentBadgeComponent} /> : null}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Drawer render functions removed — row click now navigates to ProjectDetailPage.

}

export function ProjectDetailPage({
  project,
  tasks,
  setTasks,
  setProjects,
  consultants,
  showToast,
  nav,
  KpiCardComponent,
  ChipComponent,
  PriorityBadgeComponent,
  CustomerPriorityTagComponent,
  DepartmentBadgeComponent,
  StatusChipComponent,
  PriorityChipComponent,
  AvatarComponent,
}) {
  const pt = useMemo(() => (project ? withUrgency(tasks, [project]).filter((task) => task.projectId === project.projectId) : []), [tasks, project]);
  const phases = getPhaseProgress(pt);
  const deptSummary = getDeptSummary(pt);
  const blockers = pt.filter((task) => task.status === "Blocked");
  const overdue = pt.filter((task) => daysUntil(task.dueDate) < 0 && task.status !== "Done" && task.status !== "Cancelled");
  const dEnd = project ? daysUntil(project.contractEndDate) : 0;
  const [editing, setEditing] = useState(false);
  const [newTask, setNewTask] = useState(null); // null | { title, department, assigneeId, dueDate, priority }
  const [activeUsers, setActiveUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  // Assignment ownership flag: TRACKER → Delivery is read-only;
  // SITUATION → Delivery is the source of truth and mutations flow
  // through /api/project-assignments.
  const { assignmentSource } = useSystemSettings();
  const trackerOwnsAssignments = assignmentSource === "TRACKER";
  const [pushingToTracker, setPushingToTracker] = useState(false);

  async function handlePushToTracker() {
    if (!project || pushingToTracker) return;
    setPushingToTracker(true);
    try {
      await apiClient.pushProjectToTracker(project.projectId);
      showToast?.("Push queued — tracker will reflect the project within ~30s", "success");
      // Refetch projects so the button disappears once trackerProjectId
      // is backfilled by the worker (may take a tick or two).
      if (setProjects) {
        const fresh = await apiClient.getProjects();
        setProjects(fresh.map(mapProject));
      }
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Push failed", "error");
    } finally {
      setPushingToTracker(false);
    }
  }
  // Phase C: consultant assignment picker open/closed. Must sit with the
  // other hooks (before the `if (!project) return` below) to satisfy
  // React's rules-of-hooks.
  const [assignPickerOpen, setAssignPickerOpen] = useState(false);

  useEffect(() => {
    apiClient.getActiveUsers()
      .then(d => setActiveUsers(Array.isArray(d?.users) ? d.users : Array.isArray(d) ? d : []))
      .catch(() => setActiveUsers([]));
  }, []);

  useEffect(() => {
    if (!project?.projectId) return;
    apiClient.listProjectComments(project.projectId).then(setComments).catch(() => setComments([]));
  }, [project?.projectId]);

  if (!project) return <div style={{ padding: 32, color: C.sub }}>Project not found.</div>;

  const cp = CLIENT_CUST_PRIO[project.clientName] || "Standard";

  async function markDone(id) {
    const today = new Date().toISOString().slice(0, 10);
    // Optimistic update — persist via API so it survives refresh.
    setTasks((state) => state.map((task) => (task.taskId === id ? { ...task, status: "Done", completedDate: today } : task)));
    try {
      await apiClient.updateTask(id, { status: taskStatusToEnum("Done"), completedDate: today });
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Could not mark task done", "error");
      setTasks((state) => state.map((task) => (task.taskId === id ? { ...task, status: "To Do", completedDate: null } : task)));
    }
  }

  // Tracker-critical fields that must stay non-empty on manual edit.
  // Clearing any of these via the edit drawer is blocked client-side and
  // server-side; the Push to Tracker button is disabled until they're set.
  const MANDATORY_FIELDS = [
    { key: "contractStartDate", label: "Contract Start" },
    { key: "contractEndDate",   label: "Contract End"   },
    { key: "budgetHours",       label: "Budget Hours", numeric: true },
    { key: "ratePerHour",       label: "Hourly Rate",  numeric: true },
    { key: "currency",          label: "Currency"       },
    { key: "billingFrequency",  label: "Billing Frequency" },
    { key: "priority",          label: "Priority"       },
    { key: "projectType",       label: "Project Type"   },
  ];
  const isFieldBlank = (v, numeric) =>
    v === undefined || v === null || v === "" ||
    (numeric && (Number(v) <= 0 || Number.isNaN(Number(v))));
  const missingFields = project
    ? MANDATORY_FIELDS.filter((f) => isFieldBlank(project[f.key], f.numeric))
    : [];

  async function handleFieldSave(field, value) {
    if (!setProjects) return;
    // Client-side guard: block clearing a mandatory tracker field. The
    // server also enforces this, but the inline toast is faster feedback.
    const rule = MANDATORY_FIELDS.find((f) => f.key === field);
    if (rule && isFieldBlank(value, rule.numeric)) {
      showToast?.(`${rule.label} is required — cannot be cleared`, "error");
      return;
    }
    try {
      await apiClient.updateProject(project.projectId, { [field]: value });
      const fresh = await apiClient.getProjects();
      setProjects(fresh.map(mapProject));
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Update failed", "error");
    }
  }

  // Phase C: consultant assignment editing. POST/DELETE against the
  // ProjectAssignment endpoints, then refresh projects so mapProject
  // re-emits the structured assignments array.
  async function refreshProjects() {
    if (!setProjects) return;
    const fresh = await apiClient.getProjects();
    setProjects(fresh.map(mapProject));
  }
  async function handleAssignConsultant(consultantId) {
    setAssignPickerOpen(false);
    if (!consultantId) return;
    // Pre-fill hourlyRate from the Consultant's billRate so the assignment
    // lands with a sensible per-resource rate. If billRate is 0/missing the
    // field stays null and the tracker-push writer falls back to the
    // project-level ratePerHour.
    const c = (consultants || []).find((x) => x.id === consultantId);
    const billRate = typeof c?.billRate === "number" && c.billRate > 0 ? c.billRate : null;
    try {
      await apiClient.createProjectAssignment({
        consultantId,
        projectId: project.projectId,
        // Start assignment today; over-capacity math on the server uses
        // overlapping date ranges. Default 100% allocation — users can
        // edit downward via the future inline edit flow.
        startDate: new Date().toISOString().slice(0, 10),
        allocationPct: 100,
        hourlyRate: billRate,
        // allocatedHours left null; PM fills via an edit flow so the
        // tracker-push rollup reflects the planned commitment.
      });
      await refreshProjects();
      showToast?.("Consultant assigned", "success");
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Could not assign", "error");
    }
  }
  async function handleUnassign(assignmentId) {
    if (!assignmentId) return;
    try {
      await apiClient.deleteProjectAssignment(assignmentId);
      await refreshProjects();
      showToast?.("Consultant removed", "success");
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Could not remove", "error");
    }
  }

  async function handleCreateTask() {
    if (!newTask?.title?.trim() || !newTask?.assigneeId) return;
    try {
      const created = await apiClient.createTask({
        title: newTask.title.trim(),
        description: "",
        projectId: project.projectId,
        assigneeId: newTask.assigneeId,
        department: newTask.department || "DELIVERY",
        priority: newTask.priority || "MEDIUM",
        category: "PROJECTS",
        status: newTask.status || "TO_DO",
        dueDate: newTask.dueDate,
        phaseId: newTask.phaseId || 2,
      });
      setTasks?.(state => [...state, mapTask(created)]);
      setNewTask(null);
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Create task failed", "error");
    }
  }

  async function handleToggleBlocked(task) {
    const isBlocked = task.status === "Blocked";
    const nextStatusDisplay = isBlocked ? "To Do" : "Blocked";
    const prev = task.status;
    // Optimistic update
    setTasks?.(state => state.map(t => t.taskId === task.taskId ? { ...t, status: nextStatusDisplay } : t));
    try {
      await apiClient.updateTask(task.taskId, { status: taskStatusToEnum(nextStatusDisplay) });
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Could not change status", "error");
      setTasks?.(state => state.map(t => t.taskId === task.taskId ? { ...t, status: prev } : t));
    }
  }

  async function handleDeleteTask(taskId) {
    try {
      await apiClient.deleteTask(taskId);
      setTasks?.(state => state.filter(t => t.taskId !== taskId));
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Delete task failed", "error");
    }
  }

  async function handleAddComment(payload) {
    // RichCommentInput fires onSubmit({ html, mentions, ... }); fall back to plain string.
    const body = typeof payload === "string"
      ? { content: payload }
      : { content: payload?.html || "", mentions: payload?.mentions || [] };
    if (!body.content?.trim()) return;
    try {
      const c = await apiClient.addProjectComment(project.projectId, body);
      setComments(prev => [c, ...prev]);
      setCommentDraft("");
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Comment failed", "error");
    }
  }

  async function handleDeleteComment(id) {
    try {
      await apiClient.deleteProjectComment(id);
      setComments(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Delete failed", "error");
    }
  }

  return (
    <div className="fade det">
      <div className="det-back" onClick={() => nav("projects")}>← Projects</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <div className="det-title">{project.projectName}</div>
        <ChipComponent status={project.projectStatus} />
        <PriorityBadgeComponent score={projectPriority(project)} />
        <CustomerPriorityTagComponent cp={cp} />
        {/* Push to Tracker — only shows when this project was created
            in situation-room (no trackerProjectId yet) and the ownership
            flag is flipped to SITUATION. Tooltip spells out the
            preconditions so PMs understand why it's disabled elsewhere. */}
        {!project.trackerProjectId && !trackerOwnsAssignments && (
          <button
            type="button"
            onClick={handlePushToTracker}
            disabled={pushingToTracker || missingFields.length > 0}
            title={missingFields.length
              ? `Complete required fields first: ${missingFields.map((f) => f.label).join(", ")}`
              : "Create this project in Tracker so consultants can log time against it"}
            style={{
              fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 6,
              background: missingFields.length ? "rgba(100,116,139,.12)" : "rgba(96,165,250,.12)",
              color: missingFields.length ? C.sub : C.blue,
              border: `1px solid ${missingFields.length ? "#14213d" : `${C.blue}44`}`,
              cursor: pushingToTracker ? "wait" : (missingFields.length ? "not-allowed" : "pointer"),
              marginLeft: 4,
            }}
          >{pushingToTracker ? "Queueing…" : "↗ Push to Tracker"}</button>
        )}
        {!project.trackerProjectId && trackerOwnsAssignments && (
          <span
            title="Flip assignmentSource to SITUATION before pushing"
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: ".08em",
              textTransform: "uppercase", padding: "3px 9px", borderRadius: 10,
              background: "rgba(245,158,11,.12)", color: C.yellow,
              border: `1px solid ${C.yellow}33`,
            }}
          >Not in Tracker</span>
        )}
      </div>
      <div className="det-sub">
        {project.clientId ? (
          <span
            style={{ cursor: "pointer", borderBottom: "1px dashed currentColor" }}
            onClick={() => nav("client", project.clientId)}
            title="Open client detail"
          >{project.clientName}</span>
        ) : project.clientName}
      </div>
      {missingFields.length > 0 && !project.trackerProjectId && (
        <div style={{
          marginBottom: 14, padding: "10px 14px", borderRadius: 8,
          background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.35)",
          fontSize: 12, color: "#f59e0b", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <span style={{ fontWeight: 700 }}>⚠ Incomplete for tracker push.</span>
          <span>Fill these fields below before pushing:</span>
          <span style={{ fontWeight: 600 }}>{missingFields.map((f) => f.label).join(" · ")}</span>
        </div>
      )}
      <div className="krow">
        <KpiCardComponent label="Billables" value={fmtMoney(project.billables)} />
        <KpiCardComponent label="NPS" value={(() => {
          const pn = typeof project.NPS === "number" && project.NPS > 0 ? project.NPS : null;
          const cn = typeof project.clientNps === "number" && project.clientNps > 0 ? project.clientNps : null;
          return pn ?? cn ?? "—";
        })()} />
        <KpiCardComponent label="Utilisation" value={`${project.utilizationPct}%`} />
        <KpiCardComponent label="Profit" value={`${project.profitPct}%`} />
      </div>
      {(blockers.length > 0 || overdue.length > 0) && (
        <div style={{ marginBottom: 18 }}>
          <div className="icard-title" style={{ marginBottom: 10 }}>🔴 Active Blockers &amp; Overdue Actions</div>
          {blockers.map((task) => (
            <div key={task.taskId} className="blocker-card">
              <div className="blocker-title">Blocked: {task.title}</div>
              <div className="blocker-meta"><DepartmentBadgeComponent dept={task.department} /> · {task.assignee || "Unassigned"} · Due {task.dueDate}{task.notes && ` · ${task.notes}`}</div>
            </div>
          ))}
          {overdue.map((task) => (
            <div key={task.taskId} className="blocker-card" style={{ background: "rgba(245,158,11,.05)", border: "1px solid rgba(245,158,11,.18)" }}>
              <div className="blocker-title" style={{ color: C.yellow }}>⚠ Overdue: {task.title}</div>
              <div className="blocker-meta"><DepartmentBadgeComponent dept={task.department} /> · {task.assignee || "Unassigned"} · Was due {task.dueDate}</div>
            </div>
          ))}
        </div>
      )}
      <div className="det-grid">
        <div className="icard">
          <div className="icard-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Project Details</span>
            {setProjects && (
              <button onClick={() => setEditing(e => !e)} style={{ background: "none", border: "1px solid #14213d", borderRadius: 5, padding: "2px 10px", color: "#60a5fa", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                {editing ? "Done" : "✎ Edit"}
              </button>
            )}
          </div>
          {editing ? (
            <>
              <EditRow label="Weekly Report">
                <select defaultValue={project.weeklyReportStatus || "PENDING"} onChange={e => handleFieldSave("weeklyReportStatus", e.target.value)} style={selStyle}>
                  <option value="PENDING">Pending</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="OVERDUE">Overdue</option>
                </select>
              </EditRow>
              <EditRow label="Status">
                <select defaultValue={project.projectStatus || "ON_TRACK"} onChange={e => handleFieldSave("status", e.target.value)} style={selStyle}>
                  <option value="ON_TRACK">On Track</option>
                  <option value="AT_RISK">At Risk</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </EditRow>
              <EditRow label="Contract End">
                <input type="date" min={new Date().toISOString().slice(0,10)} defaultValue={project.contractEndDate ? String(project.contractEndDate).slice(0, 10) : ""} onBlur={e => { if (e.target.value) handleFieldSave("contractEndDate", e.target.value); }} style={inpStyle} />
              </EditRow>
              <EditRow label="Deadline">
                <input type="date" min={new Date().toISOString().slice(0,10)} defaultValue={project.deliveryDeadline ? String(project.deliveryDeadline).slice(0, 10) : ""} onBlur={e => { if (e.target.value) handleFieldSave("deliveryDeadline", e.target.value); }} style={inpStyle} />
              </EditRow>
              <EditRow label="Billing">
                <select defaultValue={project.billingModel || "TIME_AND_MATERIALS"} onChange={e => handleFieldSave("billingModel", e.target.value)} style={selStyle}>
                  <option value="TIME_AND_MATERIALS">T&amp;M</option>
                  <option value="FIXED_FEE">Fixed Fee</option>
                  <option value="PRE_INVOICING">Pre-Invoicing</option>
                </select>
              </EditRow>
              <EditRow label={`Rate/hr (${project.currency || "kr"})`}>
                <input type="number" min="1" defaultValue={project.ratePerHour || ""} onBlur={e => { const v = Number(e.target.value); if (v !== project.ratePerHour) handleFieldSave("ratePerHour", v); }} style={inpStyle} />
              </EditRow>
              <EditRow label="Budget Hours">
                <input type="number" min="1" defaultValue={project.budgetHours ?? ""} onBlur={e => { const v = Number(e.target.value); if (v !== project.budgetHours) handleFieldSave("budgetHours", v); }} style={inpStyle} />
              </EditRow>
              <EditRow label="Currency">
                <select defaultValue={project.currency || "SEK"} onChange={e => handleFieldSave("currency", e.target.value)} style={selStyle}>
                  <option value="SEK">SEK</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </EditRow>
              <EditRow label="Billing Frequency">
                <select defaultValue={project.billingFrequency || "monthly"} onChange={e => handleFieldSave("billingFrequency", e.target.value)} style={selStyle}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="one_time">One-time</option>
                </select>
              </EditRow>
              <EditRow label="Priority">
                <select defaultValue={project.priority || "medium"} onChange={e => handleFieldSave("priority", e.target.value)} style={selStyle}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </EditRow>
              <EditRow label="Project Type">
                <select defaultValue={project.projectType || "development"} onChange={e => handleFieldSave("projectType", e.target.value)} style={selStyle}>
                  <option value="development">Development</option>
                  <option value="value_scan">Value Scan</option>
                  <option value="growth">Growth</option>
                  <option value="support">Support</option>
                </select>
              </EditRow>
              <EditRow label="Renewal (%)">
                <input type="number" min="0" max="100" defaultValue={project.renewalProbability || 0} onBlur={e => { const v = Number(e.target.value); if (v !== project.renewalProbability) handleFieldSave("renewalProbability", v); }} style={inpStyle} />
              </EditRow>
              <EditRow label="NPS">
                <input type="number" min="0" max="5" step="0.1" defaultValue={project.NPS || 0} onBlur={e => { const v = Number(e.target.value); if (v !== project.NPS) handleFieldSave("NPS", v); }} style={inpStyle} />
              </EditRow>
              <EditRow label="Billables (kr)">
                <input type="number" defaultValue={project.billables || 0} onBlur={e => { const v = Number(e.target.value); if (v !== project.billables) handleFieldSave("billables", v); }} style={inpStyle} />
              </EditRow>
            </>
          ) : (
            [
              ["Project Owner", project.projectOwner || "—"],
              ["Weekly Report", <ChipComponent status={project.weeklyReportStatus} />],
              ["Contract End", project.contractEndDate ? `${String(project.contractEndDate).slice(0, 10)} (${dEnd}d)` : "—"],
              ["Deadline", project.deliveryDeadline ? String(project.deliveryDeadline).slice(0, 10) : "—"],
              ["Billing", <ChipComponent status={project.billingModel} />],
              ["Rate/hr", `${project.ratePerHour || 0} ${project.currency || "kr"}`],
              ["Renewal", `${project.renewalProbability || 0}%`],
            ].map(([key, value]) => (
              <div className="det-row" key={key}><span className="det-key">{key}</span><span className="det-val">{value}</span></div>
            ))
          )}
          {/* Actuals — tracker-owned, always rendered read-only regardless of
              editing mode. These values flow one way (tracker → us) via the
              6-hourly syncActuals cron; never written back in project.upsert. */}
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 6, background: "rgba(96,165,250,.06)", border: "1px solid rgba(96,165,250,.18)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em" }}>Actuals</div>
              <span style={{ fontSize: 9, fontWeight: 700, color: C.blue, padding: "2px 6px", borderRadius: 10, background: "rgba(96,165,250,.12)", border: "1px solid rgba(96,165,250,.3)" }}>
                🔒 from Tracker
              </span>
            </div>
            <div className="det-row"><span className="det-key">Actual Cost</span><span className="det-val">
              {project.actualCost != null ? `${Math.round(project.actualCost).toLocaleString()} ${project.currency || "SEK"}` : "—"}
            </span></div>
            <div className="det-row"><span className="det-key">Actual End Date</span><span className="det-val">
              {project.actualEndDate || "—"}
            </span></div>
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className="icard-title" style={{ margin: 0 }}>Assigned Consultants</div>
                {trackerOwnsAssignments && (
                  <span
                    title="assignmentSource is TRACKER — edit in Tracker, or flip via /api/system-settings/assignment-source"
                    style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: ".08em",
                      textTransform: "uppercase",
                      padding: "2px 8px", borderRadius: 10,
                      background: "rgba(143,164,200,.12)", color: C.sub,
                      border: `1px solid ${C.dim}33`,
                    }}
                  >Driven by Tracker</span>
                )}
              </div>
              {setProjects && (
                <button
                  type="button"
                  onClick={() => { if (!trackerOwnsAssignments) setAssignPickerOpen((v) => !v); }}
                  disabled={trackerOwnsAssignments}
                  title={trackerOwnsAssignments ? "Read-only while Tracker owns assignments" : undefined}
                  style={{
                    fontSize: 11, padding: "3px 10px", borderRadius: 5,
                    background: "transparent", color: trackerOwnsAssignments ? C.dim : C.sub,
                    border: `1px solid ${C.dim}33`,
                    cursor: trackerOwnsAssignments ? "not-allowed" : "pointer",
                    opacity: trackerOwnsAssignments ? 0.5 : 1,
                  }}
                >{assignPickerOpen ? "Cancel" : "+ Assign"}</button>
              )}
            </div>

            {/* Picker shows consultants not already assigned */}
            {assignPickerOpen && (
              <div style={{
                background: "#0a1528", border: "1px solid #14213d", borderRadius: 8,
                padding: 8, marginBottom: 10, maxHeight: 240, overflowY: "auto",
              }}>
                {(consultants || [])
                  .filter((c) => !(project.assignments || []).some((a) => a.consultantId === c.id))
                  .map((c) => (
                    <div
                      key={c.id}
                      onClick={() => handleAssignConsultant(c.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                        fontSize: 12, color: C.text,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#14213d")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <AvatarComponent initials={getInit(c.name)} size={20} />
                      <span style={{ flex: 1 }}>{c.name}</span>
                      {c.role && <span style={{ fontSize: 10, color: C.dim }}>{c.role}</span>}
                    </div>
                  ))}
                {(consultants || []).filter((c) => !(project.assignments || []).some((a) => a.consultantId === c.id)).length === 0 && (
                  <div style={{ fontSize: 11, color: C.dim, padding: 8, textAlign: "center" }}>All consultants already assigned</div>
                )}
              </div>
            )}

            {/* Assignment rows (Phase C authoritative). Falls back to the
                legacy String[] for projects without ProjectAssignment rows
                yet — read-only until backfill runs. */}
            {(project.assignments || []).length > 0 ? (
              (project.assignments || []).map((a) => (
                <div
                  className="det-row"
                  key={a.id}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span style={{ color: C.text, display: "flex", alignItems: "center", gap: 7 }}>
                    <AvatarComponent initials={getInit(a.consultantName)} size={20} />
                    {a.consultantName}
                    {a.role && <span style={{ fontSize: 10, color: C.dim }}>· {a.role}</span>}
                    <span style={{ fontSize: 10, color: C.dim }}>· {a.allocationPct}%</span>
                  </span>
                  {setProjects && (
                    <button
                      type="button"
                      onClick={() => { if (!trackerOwnsAssignments) handleUnassign(a.id); }}
                      disabled={trackerOwnsAssignments}
                      title={trackerOwnsAssignments ? "Read-only while Tracker owns assignments" : "Remove assignment"}
                      style={{
                        fontSize: 10, padding: "2px 8px", borderRadius: 4,
                        background: "transparent", color: trackerOwnsAssignments ? C.dim : C.red,
                        border: `1px solid ${trackerOwnsAssignments ? C.dim : C.red}33`,
                        cursor: trackerOwnsAssignments ? "not-allowed" : "pointer",
                        opacity: trackerOwnsAssignments ? 0.5 : 1,
                      }}
                    >Remove</button>
                  )}
                </div>
              ))
            ) : (project.assignedConsultants || []).length > 0 ? (
              (project.assignedConsultants || []).map((consultant) => (
                <div className="det-row" key={consultant}>
                  <span style={{ color: C.text, display: "flex", alignItems: "center", gap: 7 }}>
                    <AvatarComponent initials={getInit(consultant)} size={20} />
                    {consultant}
                    <span style={{ fontSize: 10, color: C.dim, fontStyle: "italic" }}>· legacy (re-assign to edit)</span>
                  </span>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>
                {trackerOwnsAssignments
                  ? "No active memberships for this project in Tracker."
                  : "Unassigned — click “+ Assign” to add a consultant."}
              </div>
            )}
          </div>
        </div>
        <div className="icard">
          <div className="icard-title">Workflow Progress by Phase</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 14 }}>
            {phases.filter((phase) => phase.total > 0).map((phase) => (
              <div className="wf-row" key={phase.id}>
                <div className="wf-phase-name">{phase.short}</div>
                <div className="wf-track"><div className="wf-fill" style={{ width: `${phase.pct}%`, background: phase.pct === 100 ? C.green : phase.pct > 50 ? C.blue : C.dim }} /></div>
                <span className="wf-pct">{phase.pct}%</span>
                <span className="wf-cnt">{phase.done}/{phase.total}</span>
              </div>
            ))}
            {phases.every((phase) => phase.total === 0) && <div style={{ fontSize: 12, color: C.dim, padding: "8px 0" }}>No phases active yet — create a task below to start.</div>}
          </div>
        </div>
      </div>
      {Object.keys(deptSummary).length > 0 && (
        <div className="icard" style={{ marginTop: 14 }}>
          <div className="icard-title">Department Status</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
            {Object.entries(deptSummary).map(([dept, summary]) => (
              <div key={dept} style={{ background: "#070e20", border: "1px solid #14213d", borderRadius: 8, padding: "8px 12px", minWidth: 110 }}>
                <div style={{ marginBottom: 5 }}><DepartmentBadgeComponent dept={dept} /></div>
                <div style={{ fontSize: 11, color: C.dim }}>{summary.open} open{summary.overdue > 0 ? ` · ${summary.overdue} od` : ""}{summary.blocked > 0 ? ` · ${summary.blocked} bl` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="icard" style={{ marginTop: 14 }}>
        <div className="icard-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Full Task Breakdown</span>
          {!newTask && (
            <button onClick={() => setNewTask({ title: "", department: "DELIVERY", assigneeId: activeUsers[0]?.id || "", dueDate: new Date().toISOString().slice(0, 10), priority: "MEDIUM", phaseId: 2, status: "TO_DO" })} style={{ background: "none", border: "1px solid #14213d", borderRadius: 5, padding: "4px 10px", color: "#60a5fa", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ New Task</button>
          )}
        </div>
        {newTask && (
          <div style={{ padding: 10, background: "#070e20", border: "1px solid #1c2e52", borderRadius: 6, margin: "8px 0 14px", display: "flex", flexDirection: "column", gap: 6 }}>
            <input type="text" placeholder="Task title" autoFocus value={newTask.title} onChange={e => setNewTask(t => ({ ...t, title: e.target.value }))} style={inpStyle} />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <select value={newTask.phaseId} onChange={e => setNewTask(t => ({ ...t, phaseId: Number(e.target.value) }))} style={{ ...selStyle, flex: 1, minWidth: 120 }} title="Phase">
                {PHASES.map(ph => <option key={ph.id} value={ph.id}>{ph.short}</option>)}
              </select>
              <select value={newTask.department} onChange={e => setNewTask(t => ({ ...t, department: e.target.value }))} style={{ ...selStyle, flex: 1, minWidth: 100 }} title="Department">
                {["DELIVERY", "SALES", "HR", "FINANCE", "AUDIT", "SCOPING", "OPS"].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={newTask.priority} onChange={e => setNewTask(t => ({ ...t, priority: e.target.value }))} style={{ ...selStyle, flex: 1, minWidth: 100 }} title="Priority">
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(pr => <option key={pr} value={pr}>{pr}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <select value={newTask.status} onChange={e => setNewTask(t => ({ ...t, status: e.target.value }))} style={{ ...selStyle, flex: 1, minWidth: 120 }} title="Status">
                <option value="TO_DO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="BLOCKED">🔴 Blocked</option>
                <option value="WAITING_ON_INTERNAL">Waiting on Internal</option>
                <option value="WAITING_ON_CLIENT">Waiting on Client</option>
              </select>
              <input type="date" min={new Date().toISOString().slice(0,10)} value={newTask.dueDate} onChange={e => setNewTask(t => ({ ...t, dueDate: e.target.value }))} style={{ ...inpStyle, width: 140 }} title="Due date" />
            </div>
            <select value={newTask.assigneeId} onChange={e => setNewTask(t => ({ ...t, assigneeId: e.target.value }))} style={selStyle} title="Assignee">
              <option value="">Assign to...</option>
              {activeUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button onClick={() => setNewTask(null)} style={{ padding: "5px 10px", background: "none", border: "1px solid #14213d", borderRadius: 5, color: "#8fa4c8", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={handleCreateTask} disabled={!newTask.title.trim() || !newTask.assigneeId} style={{ padding: "5px 12px", background: "rgba(59,127,250,.14)", border: "1px solid rgba(59,127,250,.28)", borderRadius: 5, color: "#60a5fa", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", opacity: (!newTask.title.trim() || !newTask.assigneeId) ? 0.5 : 1 }}>Create</button>
            </div>
          </div>
        )}
        {phases.map((phase) => {
          const phaseTasks = pt.filter((task) => task.phaseId === phase.id && task.status !== "Cancelled");
          return (
            <div style={{ marginBottom: 18 }} key={phase.id}>
              <div className="tds-head">
                <span className="tds-name">{phase.name}</span>
                <div style={{ width: 60, height: 4, background: "#14213d", borderRadius: 2, overflow: "hidden", marginLeft: 8 }}>
                  <div style={{ width: `${phase.pct}%`, height: "100%", borderRadius: 2, background: phase.pct === 100 ? C.green : C.blue }} />
                </div>
                <span style={{ fontSize: 10, color: C.dim, marginLeft: 6 }}>{phase.done}/{phase.total}</span>
              </div>
              {phaseTasks.map((task) => {
                const isOD = daysUntil(task.dueDate) < 0 && task.status !== "Done";
                return (
                  <div key={task.taskId} className="tdr" style={isOD ? { borderLeft: `2px solid ${C.red}` } : task.status === "Blocked" ? { borderLeft: `2px solid ${C.yellow}` } : {}}>
                    <div className={`tdr-title${task.status === "Done" ? " dn" : ""}`}>{task.title}</div>
                    <DepartmentBadgeComponent dept={task.department} />
                    <StatusChipComponent status={task.status} />
                    <PriorityChipComponent priority={task.priority} />
                    {task.assignee && <span style={{ fontSize: 11, color: C.dim, minWidth: 80 }}>{task.assignee.split(" ")[0]}</span>}
                    <span className="mono" style={{ fontSize: 11, color: isOD ? C.red : C.dim, minWidth: 70 }}>{task.dueDate}</span>
                    {task.status !== "Done" && <button className="tbtn tbtn-done" style={{ fontSize: 10.5, padding: "3px 8px" }} onClick={() => markDone(task.taskId)} title="Mark done">✓</button>}
                    {task.status !== "Done" && (
                      <button onClick={() => handleToggleBlocked(task)} title={task.status === "Blocked" ? "Unblock" : "Flag as blocked"} style={{ background: task.status === "Blocked" ? "rgba(240,68,68,.18)" : "none", border: `1px solid ${task.status === "Blocked" ? "rgba(240,68,68,.4)" : "#14213d"}`, borderRadius: 4, padding: "2px 8px", color: task.status === "Blocked" ? C.red : "#8fa4c8", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>⚠</button>
                    )}
                    <button onClick={() => handleDeleteTask(task.taskId)} title="Delete task" style={{ background: "none", border: "1px solid #14213d", borderRadius: 4, padding: "2px 6px", color: C.red, fontSize: 10, cursor: "pointer" }}>✕</button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {project.riskNotes && (
        <div className="icard" style={{ marginTop: 14 }}>
          <div className="icard-title">Risk Notes</div>
          <p style={{ fontSize: 13, color: C.yellow, lineHeight: 1.75, background: "rgba(245,158,11,.06)", border: "1px solid rgba(245,158,11,.14)", borderRadius: 8, padding: "12px 14px" }}>{project.riskNotes}</p>
        </div>
      )}

      <div className="icard" style={{ marginTop: 14 }}>
        <div className="icard-title">Comments ({comments.length})</div>
        {comments.length === 0 && <div style={{ padding: "12px 0", color: C.dim, fontSize: 12 }}>No comments yet. Use @ to mention teammates.</div>}
        {comments.map((c) => (
          <div key={c.id} style={{ display: "flex", gap: 10, padding: "12px 0", borderBottom: "1px solid #14213d" }}>
            <AvatarComponent initials={c.author?.initials || getInit(c.author?.name || "")} size={26} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <strong style={{ fontSize: 12, color: "#dce8f8" }}>{c.author?.name || "Unknown"}</strong>
                <span style={{ fontSize: 10, color: C.dim }}>{new Date(c.createdAt).toLocaleString()}</span>
              </div>
              <CommentContent value={c.text} className="aud-comment-text rc-comment-body" />
            </div>
            <button onClick={() => handleDeleteComment(c.id)} title="Delete" style={{ background: "none", border: "none", color: C.dim, fontSize: 12, cursor: "pointer", opacity: 0.5, alignSelf: "flex-start" }}>✕</button>
          </div>
        ))}
        <div style={{ marginTop: 14 }}>
          <RichCommentInput
            collapsible
            placeholder="Add a comment… use @ to mention"
            rows={3}
            value={commentDraft}
            onChange={setCommentDraft}
            onSubmit={handleAddComment}
            users={activeUsers}
            mentionScopeLabel="project"
          />
        </div>
      </div>
    </div>
  );
}

function FragmentRow({ row, drawer, expanded }) {
  return (
    <>
      {row}
      {expanded ? drawer : null}
    </>
  );
}
